import React, { Component } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import PropTypes from 'prop-types';
import SecureStorage from '../utils/SecureStorage';
import AsyncStorage from '@react-native-async-storage/async-storage';

const hdkey = require('hdkey');
const bitcoreDoge = require('bitcore-lib-doge');
const EC = require('elliptic').ec;
const ec = new EC('secp256k1');

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002';

class SwapModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      step: 1, // 1=select, 2=amount, 3=confirm, 4=status
      fromCrypto: '',
      toCrypto: '',
      toNetwork: '',
      amount: '',
      estimate: null,
      currencies: [],
      minAmount: 0,
      loading: false,
      error: null,
      swap: null,
      password: '',
      swapStatus: null,
      statusPollInterval: null
    };
  }

  componentDidMount() {
    // No longer loading saved addresses - internal swaps only
  }

  componentWillUnmount() {
    if (this.state.statusPollInterval) {
      clearInterval(this.state.statusPollInterval);
    }
  }

  // Removed loadSavedAddresses - no longer needed for internal swaps
  // Removed fetchCurrencies - currencies are hardcoded for internal swaps

  fetchMinAmount = async (from, to) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/swap/min-amount?from=${from}&to=${to}`
      );
      const data = await response.json();

      if (data.success) {
        this.setState({ minAmount: data.minAmount });
      }
    } catch (error) {
      console.error('Error fetching min amount:', error);
    }
  };

  fetchEstimate = async (from, to, amount) => {
    if (!amount || parseFloat(amount) <= 0) {
      this.setState({ estimate: null });
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/swap/estimate?from=${from}&to=${to}&amount=${amount}`
      );
      const data = await response.json();

      if (data.success) {
        this.setState({ estimate: data.estimate });
      } else {
        this.setState({ estimate: null });
      }
    } catch (error) {
      console.error('Error fetching estimate:', error);
      this.setState({ estimate: null });
    }
  };

  handleFromChange = (crypto) => {
    this.setState({
      fromCrypto: crypto,
      toCrypto: '',
      amount: '',
      estimate: null
    });

    // Hardcode internal currencies instead of fetching from API
    if (crypto) {
      const internalCurrencies = [
        { ticker: 'btc', name: 'Bitcoin' },
        { ticker: 'ltc', name: 'Litecoin' },
        { ticker: 'doge', name: 'Dogecoin' }
      ].filter(c => c.ticker !== crypto.toLowerCase());

      this.setState({ currencies: internalCurrencies });
    }
  };

  handleToChange = (crypto, network = '') => {
    this.setState({
      toCrypto: crypto,
      toNetwork: network,
      estimate: null
    });

    if (this.state.fromCrypto && crypto) {
      this.fetchMinAmount(this.state.fromCrypto, crypto);
    }
  };

  handleAmountChange = (value) => {
    this.setState({ amount: value, estimate: null });

    // Debounce estimate fetch
    if (this.estimateTimeout) {
      clearTimeout(this.estimateTimeout);
    }

    this.estimateTimeout = setTimeout(() => {
      if (this.state.fromCrypto && this.state.toCrypto && value) {
        this.fetchEstimate(this.state.fromCrypto, this.state.toCrypto, value);
      }
    }, 500);
  };

  handleAmountButton = async (type) => {
    const { balances, storeId } = this.props;
    const { fromCrypto, minAmount } = this.state;

    const balance = balances[fromCrypto.toLowerCase()] || 0;

    let amount = 0;
    switch (type) {
      case 'MIN':
        amount = minAmount;
        this.handleAmountChange(amount.toFixed(8));
        break;
      case 'HALF':
        amount = balance / 2;
        this.handleAmountChange(amount.toFixed(8));
        break;
      case 'MAX':
        // Fetch dynamic fee estimate from API
        try {
          this.setState({ loading: true });
          const response = await fetch(
            `${API_BASE_URL}/utxos/fee-estimate?store_id=${storeId}&crypto=${fromCrypto}`
          );
          const data = await response.json();

          let fee;
          if (data.success && data.estimated_fee > 0) {
            // Add 20% buffer on top of estimate for safety
            fee = data.estimated_fee * 1.2;
            console.log(`Dynamic fee estimate: ${data.estimated_fee} + 20% buffer = ${fee} ${fromCrypto.toUpperCase()}`);
          } else {
            // Fallback to conservative defaults if API fails
            const fallbackFees = {
              btc: 0.0002,
              ltc: 0.002,
              doge: 2.0
            };
            fee = fallbackFees[fromCrypto.toLowerCase()] || 0.002;
            console.log(`Using fallback fee: ${fee} ${fromCrypto.toUpperCase()}`);
          }

          amount = Math.max(0, balance - fee);

          // Ensure we don't go below minimum
          if (amount < minAmount) {
            this.setState({
              error: `Insufficient balance. Need at least ${(minAmount + fee).toFixed(8)} ${fromCrypto.toUpperCase()} (${minAmount.toFixed(8)} minimum + ${fee.toFixed(8)} network fee)`,
              loading: false
            });
            return;
          }

          this.setState({ loading: false });
          this.handleAmountChange(amount.toFixed(8));
        } catch (error) {
          console.error('Error fetching fee estimate:', error);
          // Fallback to conservative defaults
          const fallbackFees = {
            btc: 0.0002,
            ltc: 0.002,
            doge: 2.0
          };
          const fee = fallbackFees[fromCrypto.toLowerCase()] || 0.002;
          amount = Math.max(0, balance - fee);
          this.setState({ loading: false });
          this.handleAmountChange(amount.toFixed(8));
        }
        break;
      default:
        amount = 0;
        this.handleAmountChange(amount.toFixed(8));
    }
  };

  // Removed handleSaveAddress - no longer needed for internal swaps

  handleStep1Next = () => {
    const { fromCrypto, toCrypto } = this.state;

    console.log('handleStep1Next called');
    console.log('fromCrypto:', fromCrypto);
    console.log('toCrypto:', toCrypto);

    if (!fromCrypto || !toCrypto) {
      this.setState({ error: 'Please select both currencies' });
      return;
    }

    console.log('Moving to step 2');
    this.setState({ step: 2, error: null }, () => {
      console.log('Now on step:', this.state.step);
    });
  };

  handleStep2Next = () => {
    const { amount, minAmount } = this.state;

    if (!amount || parseFloat(amount) < minAmount) {
      this.setState({ error: `Minimum amount is ${minAmount}` });
      return;
    }

    this.setState({ step: 3, error: null });
  };

  handleExecuteSwap = async () => {
    const { storeId, walletAddresses, payoutAddresses } = this.props;
    const {
      fromCrypto,
      toCrypto,
      toNetwork,
      amount,
      password
    } = this.state;

    // Validate password
    if (!password) {
      this.setState({ error: 'Password is required to execute swap' });
      return;
    }

    this.setState({ loading: true, error: null });

    try {
      // Verify that a wallet exists for TO crypto (backend will derive the address)
      const hasToWallet = walletAddresses[toCrypto.toLowerCase()];

      if (!hasToWallet) {
        this.setState({
          error: `No ${toCrypto.toUpperCase()} wallet found. Please create a ${toCrypto.toUpperCase()} wallet first.`,
          loading: false
        });
        return;
      }

      // Get refund address from payout addresses
      const refundAddress = payoutAddresses[fromCrypto.toLowerCase()];

      if (!refundAddress) {
        this.setState({
          error: `No ${fromCrypto.toUpperCase()} payout address configured. Please set one in Payout Settings first.`,
          loading: false
        });
        return;
      }

      // Create swap (backend will derive payout_address from store's wallet)
      const createResponse = await fetch(`${API_BASE_URL}/swap/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          store_id: storeId,
          from_crypto: fromCrypto,
          to_crypto: toCrypto,
          to_network: toNetwork,
          from_amount: parseFloat(amount),
          refund_address: refundAddress
        })
      });

      const createData = await createResponse.json();

      if (!createData.success) {
        this.setState({ error: createData.error || 'Failed to create swap', loading: false });
        return;
      }

      const swap = createData.swap;
      this.setState({ swap });

      // Build transaction
      const executeResponse = await fetch(`${API_BASE_URL}/swap/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          store_id: storeId,
          swap_id: swap.id
        })
      });

      const executeData = await executeResponse.json();

      if (!executeData.success) {
        this.setState({ error: executeData.error || 'Failed to build transaction', loading: false });
        return;
      }

      // Sign transaction using the same pattern as payout
      console.log('Signing transaction...');
      const signatures = [];
      const pubkeys = [];

      // Get stored xpriv for the FROM crypto
      const cryptoKey = `api-key-${fromCrypto.toLowerCase()}`;
      let storedXpriv = await SecureStorage.getSecureItem(cryptoKey, password);

      // Fallback to plaintext AsyncStorage if not found in SecureStorage
      if (!storedXpriv) {
        storedXpriv = await AsyncStorage.getItem(cryptoKey);
      }

      if (!storedXpriv) {
        this.setState({
          error: `No ${fromCrypto.toUpperCase()} wallet found. Please create a wallet first.`,
          loading: false
        });
        return;
      }

      const signingInfo = executeData.signing_info || [];

      // Handle Bitcoin and Litecoin BIP84 separately from DOGE
      if (fromCrypto === 'btc' || fromCrypto === 'ltc') {
        // Bitcoin/Litecoin BIP84 signing using hdkey library
        const cryptoName = fromCrypto === 'btc' ? 'Bitcoin' : 'Litecoin';
        console.log(`Using BIP84 signing for ${cryptoName}...`);
        const accountNode = hdkey.fromExtendedKey(storedXpriv);

        // Set up network configuration
        const bitcoin = require('bitcoinjs-lib');
        let network;
        if (fromCrypto === 'btc') {
          network = bitcoin.networks.bitcoin;
        } else {
          // Litecoin network config
          network = {
            messagePrefix: '\x19Litecoin Signed Message:\n',
            bech32: 'ltc',
            bip32: {
              public: 0x019da462,
              private: 0x019d9cfe,
            },
            pubKeyHash: 0x30,
            scriptHash: 0x32,
            wif: 0xb0,
          };
        }

        for (let i = 0; i < signingInfo.length; i++) {
          const { hash, address_index } = signingInfo[i];

          console.log(`Signing hash ${i+1}/${signingInfo.length}`);

          // Derive key for this address: m/84'/X'/0'/0/{index}
          const externalChain = accountNode.deriveChild(0);
          const addressNode = externalChain.deriveChild(address_index);

          const keyPair = ec.keyFromPrivate(addressNode.privateKey.toString('hex'), 'hex');
          const signature = keyPair.sign(hash, 'hex', { canonical: true });

          signatures.push(signature.toDER('hex') + '01'); // SIGHASH_ALL
          pubkeys.push(addressNode.publicKey.toString('hex'));

          console.log(`  ✓ Signed input ${i+1}/${signingInfo.length}`);
        }

        console.log(`✅ All ${signatures.length} ${cryptoName} (BIP84) inputs signed`);

      } else if (fromCrypto === 'doge') {
        // Dogecoin signing using bitcore-lib-doge
        console.log('Using Dogecoin signing (bitcore-lib-doge)...');

        const HDPrivateKey = bitcoreDoge.HDPrivateKey;
        const accountKey = HDPrivateKey.fromString(storedXpriv);

        for (let i = 0; i < signingInfo.length; i++) {
          const { hash, address_index } = signingInfo[i];

          console.log(`Signing hash ${i+1}/${signingInfo.length}`);

          // Derive the private key for this address index
          const derivedKey = accountKey.deriveChild(0).deriveChild(address_index);
          const keyPair = ec.keyFromPrivate(derivedKey.privateKey.toString('hex'), 'hex');
          const signature = keyPair.sign(hash, 'hex', { canonical: true });

          signatures.push(signature.toDER('hex')); // No SIGHASH suffix for DOGE
          pubkeys.push(derivedKey.publicKey.toString('hex'));
        }

        console.log(`✅ All ${signatures.length} hashes signed successfully`);
      } else {
        this.setState({
          error: `Unsupported cryptocurrency: ${fromCrypto}`,
          loading: false
        });
        return;
      }

      // Broadcast transaction
      const broadcastResponse = await fetch(`${API_BASE_URL}/swap/broadcast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          store_id: storeId,
          swap_id: swap.id,
          tx_skeleton: executeData.tx_skeleton,
          signatures: signatures,
          pubkeys: pubkeys
        })
      });

      const broadcastData = await broadcastResponse.json();

      if (!broadcastData.success) {
        this.setState({ error: broadcastData.error || 'Failed to broadcast transaction', loading: false });
        return;
      }

      // Move to status step
      this.setState({
        step: 4,
        swap: broadcastData.swap,
        loading: false
      });

      // Start polling for status
      this.startStatusPolling();

    } catch (error) {
      this.setState({ error: 'Failed to execute swap: ' + error.message, loading: false });
    }
  };

  startStatusPolling = () => {
    const interval = setInterval(() => {
      this.pollSwapStatus();
    }, 30000); // Poll every 30 seconds

    this.setState({ statusPollInterval: interval });

    // Initial poll
    this.pollSwapStatus();
  };

  pollSwapStatus = async () => {
    const { storeId } = this.props;
    const { swap } = this.state;

    if (!swap) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/swap/status/${swap.id}?store_id=${storeId}`
      );
      const data = await response.json();

      if (data.success) {
        this.setState({ swap: data.swap });

        // Stop polling if finished or failed
        if (data.swap.status === 'finished' || data.swap.status === 'failed') {
          if (this.state.statusPollInterval) {
            clearInterval(this.state.statusPollInterval);
            this.setState({ statusPollInterval: null });
          }

          // Notify parent to refresh balances
          if (this.props.onSwapComplete) {
            this.props.onSwapComplete();
          }
        }
      }
    } catch (error) {
      console.error('Error polling swap status:', error);
    }
  };

  handleClose = () => {
    if (this.state.statusPollInterval) {
      clearInterval(this.state.statusPollInterval);
    }

    this.setState({
      step: 1,
      fromCrypto: '',
      toCrypto: '',
      toNetwork: '',
      amount: '',
      estimate: null,
      error: null,
      swap: null,
      password: '',
      swapStatus: null,
      statusPollInterval: null
    });

    this.props.onHide();
  };

  renderStep1() {
    const { balances } = this.props;
    const {
      fromCrypto,
      toCrypto,
      currencies
    } = this.state;

    // Debug: Log balances
    console.log('SwapModal balances:', balances);

    // Get available FROM currencies (only those with balance)
    const availableFromCryptos = Object.entries(balances)
      .filter(([crypto, balance]) => {
        console.log(`Checking ${crypto}: balance = ${balance}, has balance = ${balance > 0}`);
        return balance > 0;
      })
      .map(([crypto]) => crypto.toUpperCase());

    console.log('Available FROM cryptos:', availableFromCryptos);

    return (
      <div>
        <h5>Select Currencies</h5>

        <Alert variant="info" className="mb-3">
          <small>
            <strong>Internal Swaps Only:</strong> Converted funds will be deposited to your Dijitto wallet for the selected currency.
            To withdraw, use the Z-out feature.
          </small>
        </Alert>

        <Form.Group className="mb-3">
          <Form.Label>From Currency</Form.Label>
          <Form.Select
            value={fromCrypto}
            onChange={(e) => this.handleFromChange(e.target.value)}
          >
            <option value="">Select currency...</option>
            {availableFromCryptos.map(crypto => (
              <option key={crypto} value={crypto.toLowerCase()}>
                {crypto} (Balance: {balances[crypto.toLowerCase()]?.toFixed(8)})
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        {fromCrypto && (
          <Form.Group className="mb-3">
            <Form.Label>To Currency</Form.Label>
            <Form.Select
              value={toCrypto}
              onChange={(e) => {
                const selected = currencies.find(c => c.ticker === e.target.value);
                this.handleToChange(e.target.value, selected?.network || '');
              }}
            >
              <option value="">Select currency...</option>
              {currencies.map(currency => (
                <option key={`${currency.ticker}`} value={currency.ticker}>
                  {currency.name} ({currency.ticker.toUpperCase()})
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        )}

        {toCrypto && (
          <Alert variant="success" className="mb-3">
            <small>
              <strong>Destination:</strong> Your Dijitto {toCrypto.toUpperCase()} wallet
            </small>
          </Alert>
        )}

        <div className="d-flex justify-content-between mt-4">
          <Button variant="secondary" onClick={this.handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={this.handleStep1Next}
            disabled={!fromCrypto || !toCrypto}
          >
            Next
          </Button>
        </div>
      </div>
    );
  }

  renderStep2() {
    const { amount, estimate, minAmount, fromCrypto, toCrypto } = this.state;

    return (
      <div>
        <h5>Enter Amount</h5>

        <Form.Group className="mb-3">
          <Form.Label>Amount to Swap</Form.Label>
          <Form.Control
            type="number"
            step="0.00000001"
            placeholder={`Min: ${minAmount}`}
            value={amount}
            onChange={(e) => this.handleAmountChange(e.target.value)}
          />
          <Form.Text>Minimum: {minAmount} {fromCrypto.toUpperCase()}</Form.Text>
        </Form.Group>

        <div className="mb-3">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => this.handleAmountButton('MIN')}
            className="me-2"
          >
            MIN
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => this.handleAmountButton('HALF')}
            className="me-2"
          >
            HALF
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => this.handleAmountButton('MAX')}
          >
            MAX
          </Button>
          <Form.Text className="d-block mt-1">
            <small className="text-muted">
              MAX calculates network fee based on your UTXOs
            </small>
          </Form.Text>
        </div>

        {estimate && (
          <Alert variant="info">
            <div><strong>Exchange Preview</strong></div>
            <div>You send: {parseFloat(amount).toFixed(8)} {fromCrypto.toUpperCase()}</div>
            <div>You receive: ~{estimate.toAmount.toFixed(8)} {toCrypto.toUpperCase()}</div>
            <div>Rate: 1 {fromCrypto.toUpperCase()} = {estimate.rate.toFixed(8)} {toCrypto.toUpperCase()}</div>
          </Alert>
        )}

        {amount && parseFloat(amount) < minAmount && (
          <Alert variant="warning">
            Amount is below minimum. Please enter at least {minAmount} {fromCrypto.toUpperCase()}.
          </Alert>
        )}

        <div className="d-flex justify-content-between mt-4">
          <Button variant="secondary" onClick={() => this.setState({ step: 1 })}>
            Back
          </Button>
          <Button
            variant="primary"
            onClick={this.handleStep2Next}
            disabled={!amount || parseFloat(amount) < minAmount || !estimate}
          >
            Review Swap
          </Button>
        </div>
      </div>
    );
  }

  renderStep3() {
    const { amount, estimate, fromCrypto, toCrypto, password, loading } = this.state;

    return (
      <div>
        <h5>Confirm Swap</h5>

        <Alert variant="light">
          <div className="mb-2"><strong>Swap Summary</strong></div>
          <div>From: {parseFloat(amount).toFixed(8)} {fromCrypto.toUpperCase()}</div>
          <div>To: ~{estimate?.toAmount.toFixed(8)} {toCrypto.toUpperCase()}</div>
          <div>Rate: 1 {fromCrypto.toUpperCase()} = {estimate?.rate.toFixed(8)} {toCrypto.toUpperCase()}</div>
          <div>Destination: Your Dijitto {toCrypto.toUpperCase()} Wallet</div>
          {estimate?.networkFee > 0 && (
            <div>Network Fee: {estimate.networkFee} {fromCrypto.toUpperCase()}</div>
          )}
        </Alert>

        <Form.Group className="mb-3">
          <Form.Label>Enter Password to Confirm</Form.Label>
          <Form.Control
            type="password"
            placeholder="Your store password"
            value={password}
            onChange={(e) => this.setState({ password: e.target.value })}
            autoFocus
          />
          <Form.Text>Required to sign the transaction</Form.Text>
        </Form.Group>

        <div className="d-flex justify-content-between mt-4">
          <Button variant="secondary" onClick={() => this.setState({ step: 2 })} disabled={loading}>
            Back
          </Button>
          <Button
            variant="success"
            onClick={this.handleExecuteSwap}
            disabled={!password || loading}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Executing...
              </>
            ) : (
              'Execute Swap'
            )}
          </Button>
        </div>
      </div>
    );
  }

  renderStep4() {
    const { swap } = this.state;

    if (!swap) return null;

    const isWaiting = swap.status === 'waiting';
    const isExchanging = swap.status === 'exchanging';
    const isFinished = swap.status === 'finished';
    const isFailed = swap.status === 'failed';

    return (
      <div>
        <h5>Swap Status</h5>

        <div className="mb-4">
          <div className="mb-3">
            <div className="d-flex align-items-center mb-2">
              <div style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: '#28a745', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px' }}>
                ✓
              </div>
              <span>Swap Created</span>
            </div>

            <div className="d-flex align-items-center mb-2">
              <div style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: swap.deposit_tx_hash ? '#28a745' : '#6c757d', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px' }}>
                {swap.deposit_tx_hash ? '✓' : '○'}
              </div>
              <span>Deposit Sent</span>
            </div>

            <div className="d-flex align-items-center mb-2">
              <div style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: isExchanging || isFinished ? '#28a745' : (isWaiting ? '#ffc107' : '#6c757d'), color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px' }}>
                {isExchanging || isFinished ? '✓' : (isWaiting ? '...' : '○')}
              </div>
              <span>Awaiting Confirmations</span>
            </div>

            <div className="d-flex align-items-center mb-2">
              <div style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: isFinished ? '#28a745' : (isExchanging ? '#ffc107' : '#6c757d'), color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px' }}>
                {isFinished ? '✓' : (isExchanging ? '...' : '○')}
              </div>
              <span>Exchanging</span>
            </div>

            <div className="d-flex align-items-center mb-2">
              <div style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: isFinished ? '#28a745' : '#6c757d', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px' }}>
                {isFinished ? '✓' : '○'}
              </div>
              <span>Complete</span>
            </div>
          </div>

          {swap.deposit_tx_hash && (
            <Alert variant="info">
              <small>
                Deposit TX: <code>{swap.deposit_tx_hash.substring(0, 16)}...</code>
              </small>
            </Alert>
          )}

          {swap.payout_tx_hash && (
            <Alert variant="success">
              <small>
                Payout TX: <code>{swap.payout_tx_hash.substring(0, 16)}...</code>
              </small>
            </Alert>
          )}

          {isFailed && (
            <Alert variant="danger">
              Swap failed: {swap.status_message || 'Unknown error'}
            </Alert>
          )}

          {!isFinished && !isFailed && (
            <Alert variant="info">
              Status will update automatically. You can close this window.
            </Alert>
          )}
        </div>

        <div className="d-flex justify-content-end">
          <Button variant="primary" onClick={this.handleClose}>
            {isFinished ? 'Done' : 'Close'}
          </Button>
        </div>
      </div>
    );
  }

  render() {
    const { show } = this.props;
    const { step, error } = this.state;

    console.log('SwapModal rendering, step:', step);

    return (
      <Modal show={show} onHide={this.handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Crypto Swap - Step {step}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => this.setState({ error: null })}>
              {error}
            </Alert>
          )}

          {step === 1 && this.renderStep1()}
          {step === 2 && this.renderStep2()}
          {step === 3 && this.renderStep3()}
          {step === 4 && this.renderStep4()}
        </Modal.Body>
      </Modal>
    );
  }
}

SwapModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  storeId: PropTypes.string.isRequired,
  balances: PropTypes.object,
  payoutAddresses: PropTypes.object,
  walletAddresses: PropTypes.object,
  onSwapComplete: PropTypes.func
};

SwapModal.defaultProps = {
  balances: {},
  payoutAddresses: {},
  walletAddresses: {},
  onSwapComplete: null
};

export default SwapModal;
